import React, { useMemo, useState, useCallback } from 'react';
import { Button, Card, InfoHintButton } from '../common';
import { Heading, Text, Typography } from '../common/Typography';
import { formatPlayerName } from '../../utils/nameUtils';
import { getGenderTextColor } from '../../utils/genderColors';
import { buildUniformRandomPairsAllowOdd } from '../../utils/tournamentPairing';
import { bulkCreateTournamentTeamsFromPairs } from '../../services/tournamentService';

const PAIRING_HELP =
  'Randomly pairs everyone who still needs a partner. An odd count leaves one player unpaired — run again after adding another, or pair manually above.';

/**
 * Draft-only: randomly pair unpaired entrants (odd count leaves one still without a partner).
 */
const TournamentPairingSection = ({ tournament, tournamentId, players = [], canManageTeams, onRefresh }) => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState(null);

  const status = tournament?.status;
  const solos = tournament?.tournament_solo_entrants || [];

  const playerById = useMemo(() => {
    const m = new Map();
    players.forEach((p) => m.set(p.id, p));
    return m;
  }, [players]);

  const soloPlayerIds = useMemo(() => solos.map((s) => s.player_id).filter(Boolean), [solos]);

  const runPreview = useCallback(() => {
    setLocalError(null);
    const result = buildUniformRandomPairsAllowOdd(soloPlayerIds);
    if (!result.ok) {
      setLocalError(result.error);
      setPreview(null);
      return;
    }
    setPreview({ pairs: result.pairs, leftoverId: result.leftoverId });
  }, [soloPlayerIds]);

  const clearPreview = useCallback(() => {
    setPreview(null);
    setLocalError(null);
  }, []);

  const handleApply = useCallback(async () => {
    if (!preview?.pairs?.length) return;
    setBusy(true);
    setLocalError(null);
    try {
      await bulkCreateTournamentTeamsFromPairs(tournamentId, preview.pairs);
      setPreview(null);
      if (onRefresh) await onRefresh();
    } catch (e) {
      setLocalError(e.message || 'Could not create teams');
    } finally {
      setBusy(false);
    }
  }, [preview, tournamentId, onRefresh]);

  if (!tournament || !canManageTeams || status !== 'draft') return null;

  return (
    <Card variant="default" size="small" className="shadow-brand-sm">
      <div className="mb-3 flex items-center gap-2">
        <Heading level={3} className="mb-0 text-base">
          Random pairing
        </Heading>
        <InfoHintButton text={PAIRING_HELP} ariaLabel="How random pairing works" maxWidth={300} />
      </div>

      {localError && (
        <Card variant="warning" size="small" className="mb-3 !rounded-lg !p-3">
          <Typography variant="body" className="text-sm">
            {localError}
          </Typography>
        </Card>
      )}

      {soloPlayerIds.length < 2 ? (
        <div className="rounded-xl border border-dashed border-border-medium bg-surface-subtle/30 px-4 py-5 text-center">
          <Text className="text-sm text-text-secondary">Need at least 2 players without a partner</Text>
          <Text className="mt-1 text-xs text-text-muted">
            Use Add one player above, then return here to shuffle them into teams.
          </Text>
        </div>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap gap-2">
            <Button variant="outline" size="small" type="button" onClick={runPreview} disabled={busy}>
              Preview pairs
            </Button>
            {preview && (
              <>
                <Button variant="ghost" size="small" type="button" onClick={clearPreview} disabled={busy}>
                  Clear
                </Button>
                <Button
                  variant="success"
                  size="small"
                  type="button"
                  onClick={handleApply}
                  disabled={busy || !preview.pairs.length}
                >
                  {busy ? 'Applying…' : `Create ${preview.pairs.length} team${preview.pairs.length !== 1 ? 's' : ''}`}
                </Button>
              </>
            )}
          </div>

          {preview && (
            <div className="space-y-2 rounded-xl border border-border-medium bg-surface-subtle/40 p-3 text-sm shadow-brand-sm">
              {preview.pairs.length === 0 ? (
                <Text className="text-text-muted">No pairs to create.</Text>
              ) : (
                <ul className="ml-5 list-disc space-y-1">
                  {preview.pairs.map(([a, b], i) => {
                    const pa = playerById.get(a);
                    const pb = playerById.get(b);
                    return (
                      <li key={i}>
                        <span className={getGenderTextColor(pa?.gender || '')}>
                          {pa ? formatPlayerName(pa) : a}
                        </span>
                        {' + '}
                        <span className={getGenderTextColor(pb?.gender || '')}>
                          {pb ? formatPlayerName(pb) : b}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
              {preview.leftoverId ? (
                <Text className="text-sm text-warning-dark">
                  Stays unpaired:{' '}
                  <strong className={getGenderTextColor(playerById.get(preview.leftoverId)?.gender || '')}>
                    {playerById.get(preview.leftoverId)
                      ? formatPlayerName(playerById.get(preview.leftoverId))
                      : preview.leftoverId}
                  </strong>
                </Text>
              ) : null}
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default TournamentPairingSection;
